import React, { useMemo, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, FilterIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, FileTextIcon, TerminalIcon, RefreshCwIcon, MonitorIcon, XIcon, MoreVerticalIcon } from 'lucide-react';
import { Sale, TransactionSource, TransactionStatus } from '../../../shared/types/Transaction';
import { RefundModal } from '../../refunds/components/RefundModal';
import { buildMockSales } from '../../../shared/mocks/mockSales';
import { formatMoney } from '../../../shared/lib/money';
export function Sales() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedSaleForRefund, setSelectedSaleForRefund] = useState<Sale | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Use shared mock sales fixtures (single source of truth)
  const sales: Sale[] = useMemo(() => buildMockSales(), []);
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setPaymentMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  };
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || sourceFilter !== 'all' || paymentMethodFilter !== 'all' || dateFrom !== '' || dateTo !== '' || amountMin !== '' || amountMax !== '';
  const getSourceIcon = (source: TransactionSource) => {
    const icons = {
      payment_link: <CreditCardIcon size={16} data-id="element-12363" />,
      qr_code: <QrCodeIcon size={16} data-id="element-12364" />,
      tap_to_phone: <SmartphoneIcon size={16} data-id="element-12365" />,
      invoice: <FileTextIcon size={16} data-id="element-12366" />,
      pos: <TerminalIcon size={16} data-id="element-12367" />,
      recurring: <RefreshCwIcon size={16} data-id="element-12368" />,
      virtual_terminal: <MonitorIcon size={16} data-id="element-12369" />
    };
    return icons[source];
  };
  const getSourceLabel = (source: TransactionSource) => {
    const labels = {
      payment_link: 'Payment Link',
      qr_code: 'QR Code',
      tap_to_phone: 'Tap to Phone',
      invoice: 'Invoice',
      pos: 'POS Terminal',
      recurring: 'Recurring',
      virtual_terminal: 'Virtual Terminal'
    };
    return labels[source];
  };
  const getPaymentMethodCategory = (paymentMethod: Sale['paymentMethod']) => {
    const categories: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return categories[paymentMethod.type] || paymentMethod.type;
  };
  const getPaymentMethodName = (paymentMethod: Sale['paymentMethod']) => {
    if (paymentMethod.brand) return paymentMethod.brand;
    if (paymentMethod.details) return paymentMethod.details;
    const fallback: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return fallback[paymentMethod.type] || paymentMethod.type;
  };
  const getStatusBadge = (status: TransactionStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      partially_refunded: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-12370">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sale.source === sourceFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || sale.paymentMethod.type === paymentMethodFilter;
    const saleDate = new Date(sale.date);
    const matchesDateFrom = !dateFrom || saleDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || saleDate <= new Date(dateTo + 'T23:59:59');
    const matchesAmountMin = !amountMin || sale.amount >= parseFloat(amountMin);
    const matchesAmountMax = !amountMax || sale.amount <= parseFloat(amountMax);
    return matchesSearch && matchesStatus && matchesSource && matchesPaymentMethod && matchesDateFrom && matchesDateTo && matchesAmountMin && matchesAmountMax;
  });
  const totalVolume = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);
  const totalTransactions = filteredSales.length;
  const successfulTransactions = filteredSales.filter(s => s.status === 'completed').length;
  const successRate = totalTransactions > 0 ? Math.round(successfulTransactions / totalTransactions * 100) : 0;
  // Derive currency from first sale (assume single-currency list)
  const currency = filteredSales[0]?.currency || getTenant().currency.code;
  const handleRefund = (amount: number, reason: string, notes: string) => {
    console.log('Refund initiated:', {
      transactionId: selectedSaleForRefund?.transactionId,
      amount,
      reason,
      notes
    });
    // In a real app, this would call an API to process the refund
    // For now, we'll just close the modal and show a success message
    alert(`Refund of ${formatMoney(amount, selectedSaleForRefund?.currency)} initiated successfully!`);
  };
  const handleOpenRefundModal = (sale: Sale) => {
    setSelectedSaleForRefund(sale);
    setRefundModalOpen(true);
  };
  return <div className="space-y-6" data-id="element-12371">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12372">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12373">
          <div className="text-sm font-medium text-gray-500" data-id="element-12374">
            {t('pages.Sales.total_volume')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12375">
            {formatMoney(totalVolume, currency)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12376">
          <div className="text-sm font-medium text-gray-500" data-id="element-12377">
            {t('pages.Sales.total_transactions')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12378">
            {totalTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12379">
          <div className="text-sm font-medium text-gray-500" data-id="element-12380">
            {t('pages.Sales.successful')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-12381">
            {successfulTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12382">
          <div className="text-sm font-medium text-gray-500" data-id="element-12383">
            {t('pages.Sales.success_rate')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12384">
            {successRate}%
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-12385">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-12386">
          <div className="space-y-4" data-id="element-12387">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-12388">
              <div className="flex-1 max-w-lg" data-id="element-12389">
                <div className="relative" data-id="element-12390">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-12391" />

                  <input type="text" placeholder={t('pages.Sales.attrs.search_by_transaction_id_customer_name')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-12392" />
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-12393">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showAdvancedFilters ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} data-id="element-12394">
                  <FilterIcon size={18} data-id="element-12395" />
                  {t('pages.Sales.filters')}
                  {hasActiveFilters && <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full" data-id="element-12396">
                      {[searchTerm, statusFilter !== 'all', sourceFilter !== 'all', paymentMethodFilter !== 'all', dateFrom, dateTo, amountMin, amountMax].filter(Boolean).length}
                    </span>}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-12397">
                  <DownloadIcon size={18} data-id="element-12398" />
                  {t('pages.Sales.export')}
                </button>
              </div>
            </div>
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && <div className="p-4 bg-gray-50 rounded-lg space-y-4" data-id="element-12399">
                <div className="flex items-center justify-between" data-id="element-12400">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-12401">
                    {t('pages.Sales.advanced_filters')}
                  </h3>
                  {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-12402">
                      <XIcon size={16} aria-hidden="true" data-id="element-12403" />
                      {t('pages.Sales.clear_all')}
                    </button>}
                </div>
                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-12404">
                  {/* Status Filter */}
                  <div data-id="element-12405">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12406">
                      {t('components.CampaignList.status')}
                    </label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12407">
                      <option value="all" data-id="element-12408">{t('pages.Sales.all_status')}</option>
                      <option value="completed" data-id="element-12409">
                        {t('components.getOrderStatusBadge.completed')}
                      </option>
                      <option value="pending" data-id="element-12410">
                        {t('pages.Sales.pending')}
                      </option>
                      <option value="processing" data-id="element-12411">
                        {t('pages.OrderDetail.processing')}
                      </option>
                      <option value="failed" data-id="element-12412">{t('pages.Sales.failed')}</option>
                      <option value="refunded" data-id="element-12413">
                        {t('pages.Sales.refunded')}
                      </option>
                    </select>
                  </div>
                  {/* Source Filter */}
                  <div data-id="element-12414">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12415">
                      {t('pages.SaleDetail.source')}
                    </label>
                    <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12416">
                      <option value="all" data-id="element-12417">
                        {t('pages.Sales.all_sources')}
                      </option>
                      <option value="payment_link" data-id="element-12418">
                        {t('pages.Sales.payment_link')}
                      </option>
                      <option value="qr_code" data-id="element-12419">
                        {t('pages.Sales.qr_code')}
                      </option>
                      <option value="tap_to_phone" data-id="element-12420">
                        {t('pages.Sales.tap_to_phone')}
                      </option>
                      <option value="invoice" data-id="element-12421">
                        {t('pages.SaleDetail.invoice')}
                      </option>
                      <option value="pos" data-id="element-12422">
                        {t('pages.Sales.pos_terminal')}
                      </option>
                      <option value="recurring" data-id="element-12423">
                        {t('pages.Sales.recurring')}
                      </option>
                      <option value="virtual_terminal" data-id="element-12424">
                        {t('pages.Sales.virtual_terminal')}
                      </option>
                    </select>
                  </div>
                  {/* Payment Method Filter */}
                  <div data-id="element-12425">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12426">
                      {t('pages.OrderDetail.payment_method')}
                    </label>
                    <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12427">
                      <option value="all" data-id="element-12428">
                        {t('pages.Sales.all_methods')}
                      </option>
                      <option value="card" data-id="element-12429">{t('pages.Sales.card')}</option>
                      <option value="wallet" data-id="element-12430">{t('pages.Sales.wallet')}</option>
                      <option value="bank_transfer" data-id="element-12431">
                        {t('pages.Sales.bank_transfer')}
                      </option>
                      <option value="cash" data-id="element-12432">{t('pages.Sales.cash')}</option>
                    </select>
                  </div>
                  {/* Date From */}
                  <div data-id="element-12433">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12434">
                      {t('pages.Sales.date_from')}
                    </label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12435" />
                  </div>
                  {/* Date To */}
                  <div data-id="element-12436">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12437">
                      {t('pages.Sales.date_to')}
                    </label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12438" />
                  </div>
                  {/* Amount Min */}
                  <div data-id="element-12439">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12440">
                      {t('pages.Sales.min_amount_usd')}
                    </label>
                    <input type="number" placeholder="0" value={amountMin} onChange={e => setAmountMin(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12441" />
                  </div>
                  {/* Amount Max */}
                  <div data-id="element-12442">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12443">
                      {t('pages.Sales.max_amount_usd')}
                    </label>
                    <input type="number" placeholder={t('pages.Sales.attrs.no_limit')} value={amountMax} onChange={e => setAmountMax(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12444" />
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-12445">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-12446">
            <thead className="bg-gray-50" data-id="element-12447">
              <tr data-id="element-12448">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12449">
                  {t('pages.OrderDetail.transaction_id')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12450">
                  {t('pages.Sales.date_time')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12451">
                  {t('pages.Orders.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12452">
                  {t('pages.SaleDetail.source')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12453">
                  {t('pages.Sales.payment_category')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12454">
                  {t('pages.OrderDetail.payment_method')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12455">
                  {t('pages.Sales.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12456">
                  {t('components.CampaignList.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12457">
                  {t('pages.CampaignDetails.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-12458">
              {filteredSales.map(sale => <tr key={sale.id} onClick={() => navigate(`/sale-detail/${sale.id}`)} className="hover:bg-gray-50 cursor-pointer" data-id="element-12459">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12460">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12461">
                      {sale.transactionId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12462">
                    <div className="text-sm text-gray-900" data-id="element-12463">
                      {new Date(sale.date).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-12464">
                      {new Date(sale.date).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12465">
                    <div className="text-sm text-gray-900" data-id="element-12466">
                      {sale.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-12467">
                      {sale.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12468">
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-12469">
                      {getSourceIcon(sale.source)}
                      {getSourceLabel(sale.source)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12470">
                    <div className="text-sm text-gray-900" data-id="element-12471">
                      {getPaymentMethodCategory(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12472">
                    <div className="text-sm text-gray-900" data-id="element-12473">
                      {getPaymentMethodName(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12474">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12475">
                      {formatMoney(sale.amount, sale.currency)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12476">
                    {getStatusBadge(sale.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" onClick={e => e.stopPropagation()} data-id="element-12477">
                    <div className="relative inline-block" data-id="element-12478">
                      <button aria-label="Sale actions" aria-expanded={openMenuId === sale.id} onClick={() => setOpenMenuId(openMenuId === sale.id ? null : sale.id)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-12479">
                        <MoreVerticalIcon size={18} className="text-gray-600" aria-hidden="true" data-id="element-12480" />
                      </button>

                      {/* Dropdown Menu */}
                      {openMenuId === sale.id && <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-12481" />

                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12482">
                            <button onClick={() => {
                        setOpenMenuId(null);
                        navigate(`/sale-detail/${sale.id}`);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12483">
                              <EyeIcon size={16} data-id="element-12484" />
                              {t('pages.Sales.view_details')}
                            </button>
                            {sale.status === 'completed' && <button onClick={() => {
                        setOpenMenuId(null);
                        handleOpenRefundModal(sale);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12485">
                                <RefreshCwIcon size={16} data-id="element-12486" />
                                {t('pages.SaleDetail.refund')}
                              </button>}
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSales.length === 0 && <div className="text-center py-12" data-id="element-12487">
            <p className="text-gray-500" data-id="element-12488">
              {t('pages.Sales.no_transactions_found')}
            </p>
          </div>}
      </div>
      {/* Refund Modal */}
      {selectedSaleForRefund && <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={selectedSaleForRefund.amount} transactionId={selectedSaleForRefund.transactionId} currency={getTenant().currency.code} onRefund={handleRefund} data-id="element-12489" />}
    </div>;
}